import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Percent } from "lucide-react";
import { useCurrentUser } from "@/shared/auth/useAuth";
import { Role } from "@/shared/types/role";
import { Button } from "@/shared/components/ui/button";
import { formatDateShort } from "@/shared/lib/format-date";
import { FoundingStatusBadge } from "./FoundingStatusBadge";
import { EditFoundingRatesDialog } from "./EditFoundingRatesDialog";
import { useFoundingEnrollment } from "./use-founding-enrollment";
import { currentPercent, deriveFoundingStatus } from "./types";

interface FoundingEnrollmentSectionProps {
  readonly propertyId: string;
}

/**
 * The founding-discount view on the property-detail page (ADR-0010). Shows
 * "what the property has and had": program, live status, the in-force rate,
 * plus the intro/ongoing (host) or flat (portfolio) rates, coupon, grant, and
 * any lapsed/unassigned timeline. ADMIN + FINANCE may read; only ADMIN gets the
 * "Edit rates" affordance. The whole section is gated to those two roles — the
 * enrollment is never fetched for anyone else (SUPPORT would 403).
 */
export function FoundingEnrollmentSection({ propertyId }: FoundingEnrollmentSectionProps) {
  const { t, i18n } = useTranslation();
  const currentUser = useCurrentUser();
  const viewerCanRead =
    currentUser?.role === Role.WANDERFIT_ADMIN || currentUser?.role === Role.WANDERFIT_FINANCE;
  const canManage = currentUser?.role === Role.WANDERFIT_ADMIN;

  const [editOpen, setEditOpen] = useState(false);
  const { data: enrollment } = useFoundingEnrollment(propertyId, viewerCanRead);

  if (!viewerCanRead) return null;

  const inForce = enrollment ? currentPercent(enrollment) : null;
  // Shape-based (not program-based): a PORTFOLIO_CUSTOM enrollment can now be
  // two-phase (ADR-0010 §B2), so intro/ongoing-vs-flat follows the enrollment's
  // shape — matching the edit dialog. Flat = phase FLAT / a flat percent set.
  const isTwoPhase = enrollment
    ? enrollment.phase !== "FLAT" && enrollment.flatPercent == null
    : false;
  const pct = (value: number | null): string =>
    value == null ? "—" : t("founding.enrollment.percent", { percent: value });
  const date = (value: string | null): string => formatDateShort(value, i18n.language) || "—";

  return (
    <section
      data-testid="property-founding-enrollment"
      className="space-y-4 rounded-lg border border-border bg-card p-6"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <h2 className="brand-eyebrow flex items-center gap-2">
          <Percent className="h-4 w-4" aria-hidden="true" />
          {t("founding.enrollment.title")}
        </h2>
        {canManage && enrollment && (
          <Button
            type="button"
            variant="outline"
            data-testid="founding-enrollment-edit"
            onClick={() => setEditOpen(true)}
          >
            {t("founding.enrollment.edit")}
          </Button>
        )}
      </div>

      {!enrollment ? (
        <p
          data-testid="founding-enrollment-none"
          className="font-sans text-sm text-muted-foreground"
        >
          {t("founding.enrollment.none")}
        </p>
      ) : (
        <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label={t("founding.enrollment.program")} testId="founding-enrollment-program">
            {t(`founding.program.${enrollment.program}`)}
          </Field>
          <Field label={t("founding.enrollment.status")}>
            <FoundingStatusBadge
              status={deriveFoundingStatus(enrollment)}
              testId="founding-enrollment-status"
            />
          </Field>
          <Field
            label={t("founding.enrollment.currentDiscount")}
            testId="founding-enrollment-current"
          >
            {pct(inForce)}
          </Field>

          {isTwoPhase ? (
            <>
              <Field
                label={t("founding.enrollment.introPercent")}
                testId="founding-enrollment-intro"
              >
                {pct(enrollment.introPercent)}
              </Field>
              <Field
                label={t("founding.enrollment.ongoingPercent")}
                testId="founding-enrollment-ongoing"
              >
                {pct(enrollment.ongoingPercent)}
              </Field>
              <Field
                label={t("founding.enrollment.introEndsAt")}
                testId="founding-enrollment-intro-ends"
              >
                {date(enrollment.introEndsAt)}
              </Field>
            </>
          ) : (
            <Field label={t("founding.enrollment.flatPercent")} testId="founding-enrollment-flat">
              {pct(enrollment.flatPercent)}
            </Field>
          )}

          <Field label={t("founding.enrollment.coupon")} testId="founding-enrollment-coupon" mono>
            {enrollment.appliedStripeCouponId ?? "—"}
          </Field>
          <Field label={t("founding.enrollment.grantedBy")} testId="founding-enrollment-granted-by">
            {enrollment.grantedByName ?? "—"}
          </Field>
          <Field label={t("founding.enrollment.grantedAt")} testId="founding-enrollment-granted">
            {date(enrollment.grantedAt)}
          </Field>
          {enrollment.unassignedAt && (
            <Field
              label={t("founding.enrollment.unassignedAt")}
              testId="founding-enrollment-unassigned"
            >
              {date(enrollment.unassignedAt)}
            </Field>
          )}
          {enrollment.lapsedAt && (
            <Field label={t("founding.enrollment.lapsedAt")} testId="founding-enrollment-lapsed">
              {date(enrollment.lapsedAt)}
            </Field>
          )}
        </dl>
      )}

      {canManage && enrollment && (
        <EditFoundingRatesDialog
          propertyId={propertyId}
          enrollment={enrollment}
          open={editOpen}
          onOpenChange={setEditOpen}
          onDone={() => setEditOpen(false)}
        />
      )}
    </section>
  );
}

interface FieldProps {
  readonly label: string;
  readonly testId?: string;
  readonly mono?: boolean;
  readonly children: React.ReactNode;
}

function Field({ label, testId, mono, children }: FieldProps) {
  return (
    <div>
      <dt className="font-sans text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd
        data-testid={testId}
        className={
          mono
            ? "mt-1 break-all font-mono text-xs text-foreground"
            : "mt-1 font-sans text-sm text-foreground"
        }
      >
        {children}
      </dd>
    </div>
  );
}
